import { Button, Stepper, WizardLayout } from '@platform/design-system';
import type { ShippingClient, TypedClient } from '@platform/openapi';
import { ArrowRight, CircleCheck } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { usePageContext } from '../usePageContext.ts';
import { BrandStep } from './BrandStep.tsx';
import { DeliveryStep } from './DeliveryStep.tsx';
import { OrdersStep } from './OrdersStep.tsx';
import { SETUP_STEP_ICONS } from './setup-icons.ts';
import {
  LAST_SETUP_STEP,
  SETUP_STEP_IDS,
  setupStepAt,
  SKIPPABLE_STEP_IDS,
  type SetupStepId,
} from './setup-progress.ts';
import { SETUP_FORM_ID, type StepFlow, type StepStatus, type StepViewProps } from './step-flow.ts';
import { StoreStep } from './StoreStep.tsx';
import { WelcomeStep } from './WelcomeStep.tsx';
import styles from './Setup.module.css';

const STEP_VIEWS: Readonly<Record<SetupStepId, (props: StepViewProps) => ReactNode>> = {
  welcome: WelcomeStep,
  store: StoreStep,
  brand: BrandStep,
  delivery: DeliveryStep,
  orders: OrdersStep,
};

export type SetupWizardProps = {
  api: TypedClient;
  shipping: ShippingClient;
  step: number;
  brand: ReactNode;
  guideUrl: string | null;
  onStep: (step: number) => void;
  onExit: () => void;
  onFinished: () => void;
};

const submitLabelKey = (step: number): string => {
  if (step === 0) return 'setup.start';
  return step === LAST_SETUP_STEP ? 'setup.finish' : 'setup.continue';
};

const IDLE: StepStatus = { kind: 'idle' };

const statusShownOn = (
  step: number,
  reported: { readonly step: number; readonly status: StepStatus },
): StepStatus => (reported.status.kind === 'failed' && reported.step !== step ? IDLE : reported.status);

const StatusLine = ({ status }: { status: StepStatus }) => {
  const { t } = usePageContext();
  if (status.kind === 'saving') return <span>{t('setup.saving')}</span>;
  if (status.kind === 'failed') return <span className={styles['danger']}>{status.message}</span>;
  if (status.kind === 'saved')
    return (
      <span className={styles['saved']}>
        <CircleCheck size={16} aria-hidden="true" />
        {t('setup.saved')}
      </span>
    );
  return null;
};

export const SetupWizard = ({
  api,
  shipping,
  step,
  brand,
  guideUrl,
  onStep,
  onExit,
  onFinished,
}: SetupWizardProps) => {
  const { t, notify, describeError } = usePageContext();
  const [reported, setReported] = useState<{ readonly step: number; readonly status: StepStatus }>({
    step,
    status: { kind: 'idle' },
  });
  const status = statusShownOn(step, reported);
  const setStatus = (next: StepStatus) => {
    setReported({ step, status: next });
  };
  const id = setupStepAt(step);
  const View = STEP_VIEWS[id];

  const advance = () => {
    if (step < LAST_SETUP_STEP) {
      onStep(step + 1);
      return;
    }
    notify(t('setup.finished'));
    onFinished();
  };

  const flow: StepFlow = {
    formId: SETUP_FORM_ID,
    submit: (save) => {
      setStatus({ kind: 'saving' });
      save().then(
        () => {
          setStatus({ kind: 'saved' });
          advance();
        },
        (error: unknown) => {
          setStatus({ kind: 'failed', message: describeError(error) ?? t('errors.UNKNOWN') });
        },
      );
    },
    fail: (message) => {
      setStatus({ kind: 'failed', message });
    },
  };

  return (
    <WizardLayout
      brand={brand}
      exit={
        <Button variant="ghost" onClick={onExit}>
          {t('setup.exit')}
        </Button>
      }
      stepper={
        <Stepper
          label={t('setup.stepsLabel')}
          current={step}
          reachable={LAST_SETUP_STEP}
          onSelect={onStep}
          steps={SETUP_STEP_IDS.map((stepId) => {
            const Icon = SETUP_STEP_ICONS[stepId];
            return {
              id: stepId,
              title: t(`setup.step_${stepId}`),
              description: t(`setup.step_${stepId}Help`),
              icon: <Icon size={18} aria-hidden="true" />,
            };
          })}
        />
      }
      title={t(`setup.${id}Title`)}
      lead={t(`setup.${id}Lead`)}
      status={<StatusLine status={status} />}
      actions={
        <>
          {step === 0 ? null : (
            <Button
              variant="ghost"
              onClick={() => {
                onStep(step - 1);
              }}
            >
              {t('setup.back')}
            </Button>
          )}
          {SKIPPABLE_STEP_IDS.includes(id) ? (
            <Button
              variant="outline"
              onClick={() => {
                onStep(step + 1);
              }}
            >
              {t('setup.skip')}
            </Button>
          ) : null}
          <Button
            type="submit"
            form={SETUP_FORM_ID}
            variant={step === LAST_SETUP_STEP ? 'accent' : 'primary'}
            disabled={status.kind === 'saving'}
            aria-busy={status.kind === 'saving'}
          >
            {t(submitLabelKey(step))}
            <ArrowRight size={18} aria-hidden="true" className={styles['forward']} />
          </Button>
        </>
      }
    >
      <View api={api} shipping={shipping} flow={flow} guideUrl={guideUrl} />
    </WizardLayout>
  );
};
